import { Checkbox, FormControlLabel } from "@mui/material";
import { withStyles } from "tss-react/mui";
import { convertUrlToFileUrl } from "~/common/helpers";
import { getCroppedImg } from "~/common/helpers/image";
import Button from "~/components/Button/Button";
import Input from "~/components/Input/Input";
import React, { useState, useRef, useEffect } from "react";
import ReactCrop, { PixelCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

// eslint-disable-next-line
const styles = (theme, props) => ({
  cancelButton: {
    marginLeft: "8px",
  },
  container: {
    maxWidth: "500px",
    maxHeight: "900px",
    margin: "auto",
  },
  flexCenter: {
    display: "flex",
    justifyContent: "center",
  },
  checkboxLabel: {
    color: "black",
  },
  imageContainer: {
    maxWidth: "100%",
    maxHeight: "600px",
    overflow: "hidden",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
});

interface ImageCropperProps {
  close: () => void;
  name: string;
  src: string;
  updateImage: (imgUrl: string, imgFile: File) => void;
  type: "url" | "file";
  aspectRatio?: number;
  allowAspectRatioChange?: boolean;
  classes?: Partial<
    Record<
      | "cancelButton"
      | "container"
      | "flexCenter"
      | "checkboxLabel"
      | "imageContainer",
      string
    >
  >;
}

function ImageCropper(props: ImageCropperProps) {
  const {
    close,
    src,
    updateImage,
    type,
    aspectRatio = null,
    allowAspectRatioChange = true,
  } = props;

  const [imgSrc, setSrc] = useState<string>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const canCrop = useRef<boolean>(false);
  const [crop, setCrop] = useState<PixelCrop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const [aspect, setAspect] = useState<number | null>(aspectRatio);
  const [aspectEnabled, setAspectEnabled] = useState<boolean>(
    aspectRatio !== null,
  );
  const classes = withStyles.getClasses(props);

  useEffect(() => {
    if (type === "url") {
      convertUrlToFileUrl(src)
        .then((fileUrl) => {
          setSrc(fileUrl);
        })
        .catch(() => {
          console.warn(
            "Image fetch failed. CORS settings likely to be changed.",
          );
        });
    } else {
      setSrc(src);
    }
  }, [src, type]);

  function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    const { width, height } = e.currentTarget;

    if (!aspectRatio) return;

    let cropWidth = width;
    let cropHeight = width / aspectRatio;

    if (cropHeight > height) {
      cropHeight = height;
      cropWidth = height * aspectRatio;
    }

    const x = (width - cropWidth) / 2;
    const y = (height - cropHeight) / 2;

    const newCrop: PixelCrop = {
      unit: "px",
      width: Math.floor(cropWidth),
      height: Math.floor(cropHeight),
      x: Math.floor(x),
      y: Math.floor(y),
    };

    setCrop(newCrop);
    setCompletedCrop(newCrop);
    canCrop.current = true;
  }

  function handleUpdate(c: PixelCrop) {
    if (c.width === 0 && c.height === 0) {
      canCrop.current = false;
    } else {
      canCrop.current = true;
    }
    setCrop(c);
  }

  function handleComplete(c: PixelCrop) {
    setCompletedCrop(c);
  }

  async function handleCropImage(curImage: HTMLImageElement) {
    if (!completedCrop || !curImage) {
      console.error("No crop or image available");
      return;
    }

    try {
      const img: File = await getCroppedImg(curImage, completedCrop);
      const imgURL = URL.createObjectURL(img);
      updateImage(imgURL, img);
    } catch (error) {
      console.error("Error cropping image:", error);
    }
  }

  function handleSaveImage() {
    if (imgRef.current && completedCrop) {
      handleCropImage(imgRef.current);
    }
  }

  function toggleScale() {
    if (aspectEnabled) {
      // Disable aspect ratio
      setAspectEnabled(false);
      setAspect(null);
      canCrop.current = false;
      setCrop(undefined);
      setCompletedCrop(null);
    } else {
      // Enable aspect ratio
      setAspectEnabled(true);
      setAspect(aspectRatio || 1);
      canCrop.current = false;
      setCrop(undefined);
      setCompletedCrop(null);
    }
  }

  function handleAspectChange(newAspect: string) {
    const numericAspect = parseFloat(newAspect);
    if (!isNaN(numericAspect) && numericAspect > 0) {
      setAspect(numericAspect);
    } else {
      setAspect(null);
    }
    canCrop.current = false;
    setCrop(undefined);
    setCompletedCrop(null);
  }

  return (
    <div className={classes.container}>
      <div className={classes.imageContainer}>
        {imgSrc && (
          <ReactCrop
            crop={crop}
            onChange={handleUpdate}
            onComplete={handleComplete}
            aspect={aspect}
          >
            <img
              src={imgSrc}
              ref={imgRef}
              onLoad={onImageLoad}
              alt="Crop preview"
              style={{
                maxWidth: "100%",
                maxHeight: "600px",
                display: "block",
              }}
            />
          </ReactCrop>
        )}
      </div>

      {allowAspectRatioChange && (
        <div className={classes.flexCenter} style={{ marginTop: "16px" }}>
          <FormControlLabel
            classes={{ label: classes.checkboxLabel }}
            control={
              <Checkbox
                checked={aspectEnabled}
                color="primary"
                onChange={toggleScale}
                value="ToggleScale"
                inputProps={{ "aria-label": "Aspect Ratio" }}
              />
            }
            label="Aspect Ratio"
          />

          <Input
            style={{ width: "80px" }}
            value={aspect !== null ? aspect.toString() : ""}
            onChange={handleAspectChange}
            disabled={!aspectEnabled}
            type="number"
            step={0.05}
          />
        </div>
      )}

      <div
        className={classes.flexCenter}
        style={{ marginTop: "16px", marginBottom: "16px" }}
      >
        <Button
          onClick={handleSaveImage}
          label="Crop"
          disabled={canCrop.current === false}
          data-aut="CropImage"
        />
        <Button isBlue onClick={close} label="Cancel" data-aut="CancelCrop" />
      </div>
    </div>
  );
}

export default withStyles(ImageCropper, styles);
